import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import useCrud from "../../components/common/crud/useCrud";
import lotService from "../../services/lotService";
import yardService from "../../services/yardService";
import purchaseMasterService from "../../services/purchaseMasterService";
import stockProcessingService from "../../services/stockProcessingService";
import alertService from "../../utils/alertService";

function StockProcessingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lotOptions, setLotOptions] = useState([]);
  const [yardOptions, setYardOptions] = useState([]);
  const [purchaseOptions, setPurchaseOptions] = useState([]);
  const [isLocking, setIsLocking] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState({});
  const [lotInteracted, setLotInteracted] = useState(() => {
    return (
      !!searchParams.get("lotId") ||
      sessionStorage.getItem("stockProc_interacted") === "true"
    );
  });

  // --- Summary Hook ---
  const endpoints = useMemo(() => ({ list: "/stock-processing" }), []);
  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "blContainerStockDetailsId",
    initialFilters: {
      search: searchParams.get("search") || "",
      yardId: searchParams.get("yardId") || "",
      lotId: searchParams.get("lotId") || "",
      purchaseMasterId: searchParams.get("purchaseMasterId") || "",
    },
    initialSort: { key: "createdDate", order: "DESC" },
    enabled: lotInteracted,
  });

  const isLotSelected = lotInteracted;

  const visibleRows = useMemo(() => {
    return rows.filter((item) => item.isActive !== false);
  }, [rows]);

  const handleAddStock = (item) => {
    const basePath = location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;
    const encryptedId = encrypt(String(item.blContainerStockDetailsId));
    const isLocked = item.isLocked === true;
    navigate(
      `${basePath}/bulk-stock-processing?id=${encryptedId}&lotId=${item.lotId}&isLocked=${isLocked}`,
      {
        state: { stockDetail: item },
      },
    );
  };

  const handleViewHistory = (item) => {
    const basePath = location.pathname.endsWith("/")
      ? location.pathname.slice(0, -1)
      : location.pathname;
    const isLocked = item.isLocked === true;
    navigate(
      `${basePath}/bulk-stock-processing?lotId=${item.lotId}&view=history&isLocked=${isLocked}&origin=history`,
      {
        state: { stockDetail: item },
      },
    );
  };

  const handleLockClick = async (item) => {
    const result = await alertService.editConfirm(
      "Are you sure you want to close this lot? Once closed, you will not be able to add more stock processing entries for this lot.",
    );
    if (result.isConfirmed) {
      setIsLocking(true);
      try {
        await stockProcessingService.lockLot(item.lotId);
        alertService.success("Lot closed successfully");
        refresh();
      } catch (err) {
        alertService.error(err.message || "Failed to close lot");
      } finally {
        setIsLocking(false);
      }
    }
  };

  const handleDownloadExcel = async (item) => {
    setIsDownloadingExcel((prev) => ({ ...prev, [item.lotId]: true }));
    try {
      const excelData = await stockProcessingService.downloadLotwiseExcelReport(
        item.lotId,
      );
      const blob = new Blob([excelData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `lot_${item.lotNo || item.lotId}_report.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      alertService.success("Excel report downloaded successfully");
    } catch (err) {
      alertService.error(err.message || "Failed to download Excel report");
    } finally {
      setIsDownloadingExcel((prev) => ({ ...prev, [item.lotId]: false }));
    }
  };

  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams());
    updateFilters({
      search: "",
      yardId: "",
      lotId: "",
      purchaseMasterId: "",
    });
  };

  useEffect(() => {
    if (lotInteracted) {
      sessionStorage.setItem("stockProc_interacted", "true");
    }
  }, [lotInteracted]);

  const showFormulaAlert = useCallback(() => {
    alertService.confirm(
      `
      <div class="text-left space-y-3 p-1 font-sans">
        <p class="text-sm text-slate-600 dark:text-slate-400">
          The <strong>New Original Weight (Kg)</strong> is calculated using the following formula:
        </p>
        <div class="p-4 bg-slate-55 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-800 font-mono text-xs text-slate-800 dark:text-slate-200 leading-relaxed shadow-inner">
          <div class="font-bold text-sm text-primary-600 dark:text-primary-400 mb-2">New Original Weight (Kg) =</div>
          <div class="pl-4 space-y-1">
            <div>&nbsp;&nbsp;Original Lot Weight</div>
            <div class="text-rose-500">- Transferred Weight Deduction</div>
            <div class="text-emerald-500">+ Transferred Weight Addition</div>
            <div class="text-rose-500">- Sold-Out Weight</div>
            <div class="text-emerald-500">+ Excess Weight Addition</div>
            <div class="text-rose-500">- Wastage Weight</div>
          </div>
        </div>
      </div>
      `,
      {
        title: "New Original Weight Formula",
        icon: "info",
        confirmButtonText: "Got it",
        showCancelButton: false,
      },
    );
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        key: "lotNo",
        label: "Lot No",
        sortable: true,
        render: (item) => item.lotNo || "-",
      },
      {
        key: "billNo",
        label: "Bill No",
        sortable: true,
        render: (item) => item.billNo || "-",
      },
      {
        key: "yardName",
        label: "Yard Name",
        sortable: true,
        render: (item) => item.yardName || "-",
      },
      {
        key: "originalWeight",
        label: "Original",
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono">
            {Number(item.originalWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "remainingOriginalWeight",
        label: (
          <div className="flex items-center justify-end gap-1 select-none">
            <span>New Original</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                showFormulaAlert();
              }}
              className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-500 hover:text-primary-600 transition-colors"
              title="View Formula"
            >
              <i className="material-icons text-sm">info_outline</i>
            </button>
          </div>
        ),
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span
            className="font-mono cursor-help"
            title="New Original Weight (Kg) = Original Lot Weight - Transferred Weight Deduction + Transferred Weight Addition - Sold-Out Weight + Excess Weight Addition - Wastage Weight"
          >
            {Number(
              Math.max(0, item.remainingOriginalWeight || 0),
            ).toLocaleString()}
          </span>
        ),
      },
      {
        key: "totalJointWeight",
        label: "Joint Weight",
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono text-blue-600">
            {Number(item.totalJointWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "processedWeight",
        label: "Processed",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono text-emerald-600">
            {Number(item.processedWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "remainingWeight",
        label: "Remaining",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono font-bold text-primary-600">
            {Number(item.remainingWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "totalTransferWeight",
        label: "Transfer",
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono text-purple-600">
            {Number(item.totalTransferWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "totalSalesWeight",
        label: "Sales",
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono text-indigo-600">
            {Number(item.totalSalesWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "excessWeight",
        label: "Excess",
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono text-amber-600">
            {Number(item.excessWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "wastageWeight",
        label: "Wastage",
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono text-rose-600">
            {Number(item.wastageWeight || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "procLab",
        label: "Proc. Labour",
        sortable: false,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-mono text-slate-500">
            {Number(item.procLab || 0).toLocaleString()}
          </span>
        ),
      },
      {
        key: "isLocked",
        label: "Status",
        render: (item) => {
          const isLocked = item?.isLocked === true;
          return (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                !isLocked
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                  : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
              }`}
            >
              {!isLocked ? "Open" : "Closed"}
            </span>
          );
        },
      },
    ],
    [showFormulaAlert],
  );

  useEffect(() => {
    let isMounted = true;
    const fetchFilters = async () => {
      try {
        const [lotsRes, yardsRes, purchasesRes] = await Promise.all([
          lotService.getActiveLots({ showLocked: true }),
          yardService.getActiveYards(),
          purchaseMasterService.getActivePurchaseMasters(),
        ]);
        if (!isMounted) return;

        const lots = lotsRes?.items || (Array.isArray(lotsRes) ? lotsRes : []);
        setLotOptions([
          { label: "Show All", value: "" },
          ...lots.map((lot) => ({
            label: lot.lotNo,
            value: String(lot.lotId),
          })),
        ]);

        const yards =
          yardsRes?.items ||
          yardsRes?.data ||
          (Array.isArray(yardsRes) ? yardsRes : []);
        setYardOptions([
          { label: "All Yards", value: "" },
          ...yards.map((yard) => ({
            label: yard.yardNo || yard.yardName,
            value: String(yard.yardId),
          })),
        ]);

        const purchases =
          purchasesRes?.items ||
          purchasesRes?.data ||
          (Array.isArray(purchasesRes) ? purchasesRes : []);
        setPurchaseOptions([
          { label: "All Purchases", value: "" },
          ...purchases.map((p) => ({
            label: p.billNo,
            value: String(p.purchaseMasterId || p.id),
          })),
        ]);
      } catch (err) {
        console.error("Failed to fetch filters", err);
      }
    };
    fetchFilters();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("page", String(filters.page || 1));
    next.set("limit", String(filters.limit || 10));
    next.set("sort", filters.sort || "createdDate");
    next.set("order", filters.order || "DESC");
    if (filters.search) next.set("search", filters.search);
    if (filters.yardId) next.set("yardId", String(filters.yardId));
    if (filters.lotId) next.set("lotId", String(filters.lotId));
    if (filters.purchaseMasterId)
      next.set("purchaseMasterId", String(filters.purchaseMasterId));
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  const renderMobileCard = (item, index) => {
    const isLocked = item?.isLocked === true;
    return (
      <div
        key={item.blContainerStockDetailsId || index}
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/60"
      >
        <div className="flex items-start justify-between mb-3 border-b border-slate-100 dark:border-slate-800 pb-2">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold text-slate-900 dark:text-white">
                {item.lotNo || "-"}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                  !isLocked
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                    : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                }`}
              >
                {!isLocked ? "Open" : "Closed"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
              Bill:{" "}
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {item.billNo || "-"}
              </span>
              <span className="mx-1.5">•</span>
              Yard:{" "}
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {item.yardName || "-"}
              </span>
            </div>
          </div>

          <div className="flex gap-1.5">
            <Button
              variant="primary"
              size="sm"
              className="flex h-8 w-8 items-center justify-center rounded-full p-0"
              onClick={() => handleAddStock(item)}
              title="Add Bulk Processing"
              disabled={isLocked}
            >
              <i className="material-icons text-base">add</i>
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex h-8 w-8 items-center justify-center rounded-full p-0"
              onClick={() => handleViewHistory(item)}
              title="View History"
            >
              <i className="material-icons text-base">history</i>
            </Button>
            <Button
              variant="success"
              size="sm"
              className="flex h-8 w-8 items-center justify-center rounded-full p-0"
              onClick={() => handleDownloadExcel(item)}
              title="Download Excel Report"
              disabled={!!isDownloadingExcel[item.lotId]}
            >
              {isDownloadingExcel[item.lotId] ? (
                <svg
                  className="h-4 w-4 animate-spin text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <i className="material-icons text-base">file_download</i>
              )}
            </Button>
            <Button
              variant={isLocked ? "secondary" : "danger"}
              size="sm"
              className="flex h-8 w-8 items-center justify-center rounded-full p-0"
              onClick={() => handleLockClick(item)}
              title={isLocked ? "Lot Closed" : "Close Lot"}
              disabled={isLocked || isLocking}
            >
              <i className="material-icons text-base">
                {isLocked ? "lock" : "lock_open"}
              </i>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Original
            </span>
            <span className="font-mono font-bold text-slate-700 dark:text-slate-300 mt-0.5">
              {Number(item.originalWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Joint
            </span>
            <span className="font-mono font-bold text-blue-600 dark:text-blue-400 mt-0.5">
              {Number(item.totalJointWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Processed
            </span>
            <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
              {Number(item.processedWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-primary-500 mt-0.5 font-bold">
              Remaining
            </span>
            <span className="font-mono font-black text-primary-600 dark:text-primary-400 mt-0.5">
              {Number(item.remainingWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Transfer
            </span>
            <span className="font-mono font-bold text-purple-600 mt-0.5">
              {Number(item.totalTransferWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Sales
            </span>
            <span className="font-mono font-bold text-indigo-600 mt-0.5">
              {Number(item.totalSalesWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Excess
            </span>
            <span className="font-mono font-bold text-amber-600 dark:text-amber-400 mt-0.5">
              {Number(item.excessWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Wastage
            </span>
            <span className="font-mono font-bold text-rose-600 dark:text-rose-400 mt-0.5">
              {Number(item.wastageWeight || 0).toLocaleString()} Kg
            </span>
          </div>
          <div className="flex flex-col border-b border-slate-50 dark:border-slate-800/50 pb-1.5 col-span-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 font-bold">
              Proc. Labour Cost
            </span>
            <span className="font-mono font-bold text-slate-500 dark:text-slate-400 mt-0.5">
              {Number(item.procLab || 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            Stock <span className="text-primary-600">Processing</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Manage your inventory transformations and labour costs
          </p>
        </div>
      </div> */}

      {error ? <ErrorAlert message={error} /> : null}

      <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:border-sky-800/30 dark:bg-sky-900/10 dark:text-sky-400">
        Note: all weight values are in KG.
      </div>

      <CrudTable
        columns={tableColumns}
        rows={visibleRows}
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={updateFilters}
        showActions={true}
        renderActions={(item) => {
          const isLocked = item?.isLocked === true;
          return (
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                className="flex h-8 w-8 items-center justify-center rounded-full p-0"
                onClick={() => handleAddStock(item)}
                title="Add Bulk Processing"
                disabled={isLocked}
              >
                <i className="material-icons text-base">add</i>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex h-8 w-8 items-center justify-center rounded-full p-0"
                onClick={() => handleViewHistory(item)}
                title="View History"
              >
                <i className="material-icons text-base">history</i>
              </Button>
              <Button
                variant="success"
                size="sm"
                className="flex h-8 w-8 items-center justify-center rounded-full p-0"
                onClick={() => handleDownloadExcel(item)}
                title="Download Excel Report"
                disabled={!!isDownloadingExcel[item.lotId]}
              >
                {isDownloadingExcel[item.lotId] ? (
                  <svg
                    className="h-4 w-4 animate-spin text-white"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <i className="material-icons text-base">file_download</i>
                )}
              </Button>
              <Button
                variant={isLocked ? "secondary" : "danger"}
                size="sm"
                className="flex h-8 w-8 items-center justify-center rounded-full p-0"
                onClick={() => handleLockClick(item)}
                title={isLocked ? "Lot Closed" : "Close Lot"}
                disabled={isLocked || isLocking}
              >
                <i className="material-icons text-base">
                  {isLocked ? "lock" : "lock_open"}
                </i>
              </Button>
            </div>
          );
        }}
        pagination={pagination}
        sortConfig={sortConfig}
        onSort={changeSort}
        onPageChange={changePage}
        searchPlaceholder="Search BL, Lot..."
        noDataMessage={
          !isLotSelected
            ? "Please select a Lot filter to view processing data"
            : "No processing records found for this lot"
        }
        tableScrollHeight="50vh"
        toolbarFilters={
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 w-full sm:w-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 min-w-0">
              <SearchableDropdown
                name="yardId"
                value={String(filters.yardId || "")}
                options={yardOptions}
                placeholder="Yard"
                onChange={(e) => updateFilters({ yardId: e.target.value })}
              />
              <SearchableDropdown
                name="purchaseMasterId"
                value={String(filters.purchaseMasterId || "")}
                options={purchaseOptions}
                placeholder="Purchase"
                onChange={(e) =>
                  updateFilters({ purchaseMasterId: e.target.value })
                }
              />
              <SearchableDropdown
                name="lotId"
                value={
                  !lotInteracted && filters.lotId === ""
                    ? "SELECT_PROMPT"
                    : String(filters.lotId || "")
                }
                options={lotOptions}
                placeholder="Select Lot"
                autoFocus={true}
                highAttention={!lotInteracted}
                onChange={(e) => {
                  setLotInteracted(true);
                  updateFilters({ lotId: e.target.value });
                }}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="px-3 border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300 h-9 sm:h-[30px] flex items-center justify-center whitespace-nowrap shrink-0"
              onClick={handleClearFilters}
              title="Clear all filters"
            >
              <i className="material-icons text-sm mr-1">filter_alt_off</i>
              Clear
            </Button>
          </div>
        }
      />
    </div>
  );
}

export default StockProcessingPage;
